/* Almila welcome offer (sections/almila-welcome-offer.liquid).
   - Waits for the cookie choice, then shows the "−10%" sticker after the teaser delay.
   - Larger screens with a mouse: the card also opens by itself once per visit, from the second page on, after the open
     delay or when the pointer leaves towards the address bar. It opens without moving focus.
   - Phones: only a tap on the sticker opens it, as a bottom sheet.
   - Closing hides the offer for the snooze days; a sign-up hides it for good (localStorage, per browser).
   - At the page's end the sticker steps aside while the footer's last row is where it sits (data-at-end, see the CSS).
   - After Shopify's customer form is posted (?customer_posted=true, or form.posted_successfully? in Liquid), it opens
     on the code. Nothing is sent from here: the form posts natively. */
(() => {
  if (customElements.get('almila-welcome-offer')) return;
  const KEY = 'almila-welcome';
  const VIEWS = 'almila-welcome-views';
  const AUTO = 'almila-welcome-auto';
  const DRAWERS = '.show-sidebar, .show-sidebar-main, .show-sidebar-filter';
  const PAGE_END = '.almila-footer__bottom'; // the footer's last row: the copyright and the cookie settings button
  const STICKER_ZONE = '-85% 0px 0px 0px'; // the bottom 15% of the window, where the sticker sits
  const read = (store, key) => { try { return store.getItem(key); } catch (e) { return null; } };
  const write = (store, key, value) => { try { store.setItem(key, value); } catch (e) { /* private mode */ } };

  class AlmilaWelcomeOffer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.teaser = this.querySelector('.almila-welcome__teaser');
      this.opener = this.querySelector('.almila-welcome__sticker');
      if (!this.dialog || !this.teaser) return;
      this.wide = window.matchMedia('(min-width: 768px) and (pointer: fine)');
      this.cookieReady = false;
      this.autoWaiting = false;
      this.events = new AbortController();
      this.watchPageEnd();

      this.opener.addEventListener('click', () => this.open(true));
      this.querySelector('[data-welcome-dismiss]').addEventListener('click', () => { this.snooze(); this.teaser.hidden = true; });
      this.dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-welcome-close]') || event.target === this.dialog) this.dialog.close();
        const copy = event.target.closest('[data-welcome-copy]');
        if (copy) this.copy(copy);
      });
      this.dialog.addEventListener('keydown', (event) => { if (event.key === 'Escape' && !this.dialog.matches(':modal')) this.dialog.close(); });
      this.dialog.addEventListener('close', () => this.closed());
      // Shopify's spam check (hCaptcha) may show a challenge after the submit. A modal dialog sits in the top layer above
      // it, so the sheet turns non-modal first; the form stays where it is and posts as usual.
      this.dialog.addEventListener('submit', () => {
        if (!this.dialog.matches(':modal')) return;
        this.dataset.sending = 'true';
        this.dialog.close();
        this.dialog.setAttribute('data-sending', '');
        this.dialog.show();
      }, true);

      if (this.hasAttribute('data-design-mode')) {
        this.teaser.hidden = true;
        this.afterCookieChoice(() => {
          this.teaser.hidden = false;
          this.warm();
          if (this.editorSelected) this.open(false);
        });
        // The editor re-renders the section on every change: the old element's document listeners go with it.
        const { signal } = this.events;
        document.addEventListener('shopify:section:select', (event) => {
          if (event.target.contains(this)) { this.editorSelected = true; this.open(false); }
        }, { signal });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.target.contains(this)) { this.editorSelected = false; this.dialog.close(); }
        }, { signal });
        return;
      }

      const params = new URLSearchParams(location.search);
      const state = this.querySelector('[data-welcome-state]')?.dataset.welcomeState;
      if (state === 'success' || (params.get('customer_posted') === 'true' && state !== 'error')) {
        this.success();
        params.delete('customer_posted');
        const query = params.toString();
        history.replaceState(history.state, '', location.pathname + (query ? `?${query}` : '') + location.hash.replace('#AlmilaWelcomeForm', ''));
        this.afterCookieChoice(() => this.open(true));
        return;
      }
      if (state === 'error') { this.afterCookieChoice(() => this.open(true)); return; }
      if (this.remembered()) return;

      const views = Number(read(sessionStorage, VIEWS) || 0) + 1;
      write(sessionStorage, VIEWS, String(views));
      this.afterCookieChoice(() => this.schedule(views));
    }

    disconnectedCallback() {
      this.events?.abort();
      this.endObserver?.disconnect();
      clearTimeout(this.cookieTimer);
      clearTimeout(this.teaserTimer);
      clearTimeout(this.autoTimer);
    }

    // The sticker covers nothing at the page's end, so the footer needs no room kept free under its last row.
    watchPageEnd() {
      const end = document.querySelector(PAGE_END);
      if (!end || !('IntersectionObserver' in window)) return;
      this.endObserver = new IntersectionObserver((entries) => {
        this.teaser.toggleAttribute('data-at-end', entries[entries.length - 1].isIntersecting);
      }, { rootMargin: STICKER_ZONE });
      this.endObserver.observe(end);
    }

    remembered() {
      try {
        const saved = JSON.parse(read(localStorage, KEY) || 'null');
        return !!saved && (saved.state === 'done' || saved.until > Date.now());
      } catch (e) { return false; }
    }

    snooze() {
      const days = Number(this.dataset.snoozeDays) || 14;
      write(localStorage, KEY, JSON.stringify({ state: 'snoozed', until: Date.now() + days * 864e5 }));
    }

    afterCookieChoice(run) {
      const bar = document.getElementById('cookiebarBox');
      const ready = () => { if (this.isConnected) { this.cookieReady = true; run(); } };
      // The theme hides the bar (style.display = 'none') once a choice is made. That also counts: in the theme editor
      // the choice's cookie may not be readable (a cross-site frame), and a re-rendered offer would wait for ever.
      if (!bar || bar.style.display === 'none' || /(?:^|;\s*)seen-cookiePopup=(?:yes|rejected)(?:;|$)/.test(document.cookie)) { ready(); return; }
      const once = () => {
        document.removeEventListener('click', onClick, true);
        this.cookieTimer = setTimeout(ready, 1500);
      };
      const onClick = (event) => { if (event.target.closest('#cookieBoxok, #cookieBoxReject')) once(); };
      document.addEventListener('click', onClick, { capture: true, signal: this.events.signal });
    }

    // Something else is open: another dialog or one of the theme's drawers (the cart, the phone menu and Shopify's
    // filters mark <body>; our wardrobe filter marks its own root).
    blocked() {
      return !this.cookieReady || document.body.matches(DRAWERS) ||
        !!document.querySelector('dialog[open]:not(.almila-welcome__dialog), .almila-wardrobe.is-filter-open');
    }

    schedule(views) {
      this.teaserTimer = setTimeout(() => { if (!this.dialog.open) { this.teaser.hidden = false; this.warm(); } }, (Number(this.dataset.teaserDelay) || 0) * 1000);
      if (this.dataset.autoOpen !== 'true' || !this.wide.matches || views < 2 || read(sessionStorage, AUTO)) return;
      const auto = () => {
        if (this.blocked()) { this.autoWaiting = true; return; }
        this.autoWaiting = false;
        document.removeEventListener('mouseout', onLeave);
        clearTimeout(this.autoTimer);
        if (this.dialog.open || this.remembered()) return;
        write(sessionStorage, AUTO, '1');
        this.open(false);
      };
      const onLeave = (event) => { if (!event.relatedTarget && event.clientY <= 0) auto(); };
      const resume = () => {
        if (this.autoWaiting && !this.blocked()) {
          clearTimeout(this.autoTimer);
          this.autoTimer = setTimeout(auto, 1500);
        }
      };
      this.autoTimer = setTimeout(auto, (Number(this.dataset.openDelay) || 20) * 1000);
      document.addEventListener('mouseout', onLeave, { signal: this.events.signal });
      document.addEventListener('toggle', resume, { capture: true, signal: this.events.signal });
      document.addEventListener('close', resume, { capture: true, signal: this.events.signal });
    }

    warm() {
      // The photo is lazy inside a closed dialog; fetch it once the offer is in view, so the card opens complete.
      this.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    }

    open(byVisitor) {
      if (this.dialog.open || this.blocked()) return;
      this.warm();
      this.teaser.hidden = true;
      if (!this.wide.matches) {
        this.dialog.showModal();
        return;
      }
      const before = document.activeElement;
      this.dialog.show();
      if (byVisitor) {
        (this.dialog.querySelector('input:not([type="hidden"]), .almila-welcome__copy') || this.dialog.querySelector('[data-welcome-close]')).focus();
      } else if (before && before !== document.body) {
        before.focus({ preventScroll: true }); // opened by itself: show() moves focus, so give it back
      } else {
        document.activeElement?.blur();
      }
    }

    closed() {
      if (this.dataset.done || this.dataset.sending) return;
      this.snooze();
      this.teaser.hidden = false;
      if (this.contains(document.activeElement) || document.activeElement === document.body) this.opener.focus({ preventScroll: true });
    }

    success() {
      const template = this.querySelector('template[data-welcome-success]');
      const form = this.querySelector('form');
      if (!template || !form) return;
      form.replaceChildren(template.content.cloneNode(true));
      // The apply address is built here, not printed in the HTML, so crawlers find no /discount/ link (TE-18).
      const apply = form.querySelector('[data-welcome-apply]');
      if (apply) apply.href = `/discount/${encodeURIComponent(this.dataset.code)}?redirect=${window.location.pathname}`;
      this.dataset.done = 'true';
      write(localStorage, KEY, JSON.stringify({ state: 'done' }));
      if (window.google_tag_manager && Array.isArray(window.dataLayer)) window.dataLayer.push({ event: 'almila_welcome_signup' });
    }

    copy(button) {
      const code = this.dataset.code;
      const status = this.querySelector('[data-welcome-status]');
      const done = () => {
        const label = button.textContent;
        button.textContent = button.dataset.copied;
        if (status) status.textContent = button.dataset.copied;
        setTimeout(() => { button.textContent = label; }, 2500);
      };
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(code).then(done, () => {});
    }
  }

  customElements.define('almila-welcome-offer', AlmilaWelcomeOffer);
})();
